import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import Tooltip from 'antd/lib/tooltip';
import { Flex } from 'antd/lib';
import { EditOutlined } from '@ant-design/icons';
import { RootState } from '../../reducers/reducers';
import { selectNotificationIntegration } from '../../selectors/selectors';
import { AppComponentProps } from '../../typescriptHelpers';
import { IntegrationInstallationId, IntegrationType } from '../../types';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { codeStyle } from '../../helpers/displayHelpers';
import { integrationUpdatableForIntegrationType } from '../../helpers/integrationHelpers';
import { tryUpdateIntegrationRequest, UpdateIntegrationRequestDataType } from '../../actions/integrations';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { globalMessage } from '../../utils/globalMessage';
import EditIntegrationModal from '../EditIntegrationModal';
import IntegrationIcon from '../icons/IntegrationIcon';

const PrimaryChannelsHeader: React.FC<AppComponentProps> = ({ orgId, appId }) => {
    const dispatch = useDispatch();
    const notificationsIntegration = useSelector((state: RootState) => selectNotificationIntegration(state, appId));
    const primaryChannelNames = React.useMemo(() => {
        const data = notificationsIntegration?.data as { primaryChannels?: Array<{ name: string }> } | undefined;
        return (data?.primaryChannels ?? []).map((c) => c.name);
    }, [notificationsIntegration]);

    const [modalOpen, setModalOpen] = React.useState(false);
    const updateType =
        notificationsIntegration ? integrationUpdatableForIntegrationType(notificationsIntegration.id) : undefined;
    const updateIsLoading = useIsLoading(updateType ?? '', notificationsIntegration?.installationId ?? '');
    const updateSuccess = useUpdated(updateType ?? '', notificationsIntegration?.installationId ?? '');
    const updateError = useError(updateType ?? '', notificationsIntegration?.installationId ?? '');
    React.useEffect(() => {
        if (updateError && modalOpen) {
            globalMessage.error(updateError);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateError]);
    React.useEffect(() => {
        if (updateSuccess && modalOpen) {
            setModalOpen(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateSuccess]);

    const handleUpdateIntegration = (
        installationId: IntegrationInstallationId,
        integrationType: IntegrationType,
        data: UpdateIntegrationRequestDataType,
    ) => {
        const type = integrationUpdatableForIntegrationType(integrationType);
        if (!type) {
            globalMessage.error('This integration does not support updating.');
            return;
        }
        dispatch(tryUpdateIntegrationRequest(type, { orgId, appId, integrationId: installationId, data }));
    };

    if (!notificationsIntegration || primaryChannelNames.length === 0) {
        return null;
    }

    return (
        <>
            <Flex
                align="center"
                gap={8}
                wrap="wrap"
                style={{
                    backgroundColor: antGrey.grey2,
                    border: `1px solid ${antGrey.grey5}`,
                    borderRadius: 4,
                    padding: '10px 14px',
                }}
            >
                <IntegrationIcon integrationId={notificationsIntegration.id} />
                <Tooltip title="All notifications will go to your primary channel(s), unless an override is defined in an individual notification's settings.">
                    <Text
                        style={{
                            textDecoration: 'underline dotted',
                            textUnderlineOffset: 4,
                            cursor: 'default',
                        }}
                    >
                        Primary {notificationsIntegration.id === IntegrationType.MicrosoftTeams ? 'Teams' : 'Slack'}{' '}
                        channel(s):
                    </Text>
                </Tooltip>
                {primaryChannelNames.map((name) => (
                    <Text code key={name} style={codeStyle('14px')}>
                        {name}
                    </Text>
                ))}
                <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined style={{ color: runwayColors.primary }} />}
                    onClick={() => setModalOpen(true)}
                />
            </Flex>
            <EditIntegrationModal
                orgId={orgId}
                appId={appId}
                integration={notificationsIntegration}
                open={modalOpen}
                isLoading={updateIsLoading}
                updateIntegration={handleUpdateIntegration}
                onCancel={() => setModalOpen(false)}
            />
        </>
    );
};

export default PrimaryChannelsHeader;
